import { useState } from "react";
import { Download, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { API, errMsg, http } from "@/lib/api";
import { moodById } from "@/lib/event";
import { Panel } from "@/components/host/HostOverview";

const LABEL = { yes: "Attending", maybe: "Maybe", no: "Declined" };

function Row({ r, reload }) {
  const [form, setForm] = useState({ name: r.name, response: r.response, party_size: r.party_size || 1, dietary: r.dietary, note: r.note });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      await http.put(`/host/rsvps/${r.id}`, { ...form, party_size: form.response === "no" ? 0 : Number(form.party_size) || 1 });
      toast.success(`Updated ${form.name}`);
      await reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!window.confirm(`Remove ${r.name}'s RSVP? This can't be undone.`)) return;
    await http.delete(`/host/rsvps/${r.id}`).catch(() => {});
    reload();
  };
  return (
    <li className="grid gap-3 rounded-2xl border border-[rgba(181,148,82,.45)] bg-[var(--ivory)] p-4 md:grid-cols-12 md:items-start" data-testid={`host-rsvp-${r.id}`}>
      <div className="md:col-span-3">
        <input aria-label="Name" className="field font-semibold" value={form.name} onChange={(e) => set("name", e.target.value)} />
        <p className="mt-1 text-xs text-[#3d5a51]">{[moodById(r.mood)?.name, r.rules_accepted && "Usool manzoor"].filter(Boolean).join(" · ") || "No mood chosen"}</p>
      </div>
      <select aria-label="Response" className="field md:col-span-2" value={form.response} onChange={(e) => set("response", e.target.value)}>
        {Object.entries(LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <input aria-label="People" type="number" min={0} max={20} className="field md:col-span-1" value={form.response === "no" ? 0 : form.party_size} disabled={form.response === "no"} onChange={(e) => set("party_size", e.target.value)} />
      <textarea aria-label="Dietary" rows={1} placeholder="Dietary" className="field md:col-span-2" value={form.dietary} onChange={(e) => set("dietary", e.target.value)} />
      <textarea aria-label="Note" rows={1} placeholder="Note" className="field md:col-span-2" value={form.note} onChange={(e) => set("note", e.target.value)} />
      <div className="flex gap-2 md:col-span-2">
        <button type="button" className="btn-primary min-h-[44px] flex-1 px-4 text-sm" onClick={save} disabled={busy}>{busy ? "…" : "Save"}</button>
        <button type="button" className="btn-ghost min-h-[44px] px-3" onClick={remove} aria-label={`Delete ${r.name}`}><Trash2 className="h-4 w-4" /></button>
      </div>
    </li>
  );
}

export default function HostRsvps({ rsvps, reload }) {
  const items = rsvps.items || [];
  return (
    <Panel
      title={`Guest list (${items.length})`}
      action={<a href={`${API}/host/rsvps.csv`} className="btn-ghost min-h-[44px] text-sm" data-testid="export-csv-button"><Download className="h-4 w-4" aria-hidden="true" /> Export CSV</a>}
    >
      {items.length ? (
        <ul className="flex flex-col gap-3" data-testid="host-rsvp-list">
          {items.map((r) => <Row key={`${r.id}-${r.updated_at}`} r={r} reload={reload} />)}
        </ul>
      ) : <p className="text-sm">No RSVPs yet. Share the invitation link and they’ll appear here.</p>}
    </Panel>
  );
}
